<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content vasion-import-modal">
      <div class="modal-header">
        <h2 class="modal-header-title">Import from Vasion</h2>
      </div>

      <div class="modal-body-scrollable">
        <div class="vasion-import-content">
          <!-- Left: Folders -->
          <div class="vasionFolders-section">
            <h3 class="section-title">Folders</h3>
            <div class="vasionFolders-list">
              <div v-if="loadingStates.vasionFolders" class="folder-item" style="justify-content: center; font-style: italic; display: flex; align-items: center">Loading folders...</div>

              <div v-else v-for="folder in vasionFolders" :key="folder.id" class="folder-item" :class="{ active: selectedFolder === folder.id }" @click="selectFolder(folder.id)">
                <i class="fas fa-folder"></i>
                <span>{{ folder.name }}</span>
              </div>
            </div>
          </div>

          <!-- Right: Files -->
          <div class="files-section">
            <h3 class="section-title">Files</h3>

            <!-- Search box -->
            <input type="text" v-model="searchQuery" placeholder="Search files..." class="file-search" />
            <!-- Select All Checkbox -->
            <div class="select-all-container">
              <input type="checkbox" :checked="areAllFilesSelected" @change="toggleSelectAll" />
              <label style="margin-left: 8px">
                Select All
                <span v-if="filteredFiles.length"
                  ><strong>({{ filteredFiles.length }} files)</strong></span
                >
              </label>
            </div>

            <div class="files-list">
              <div v-if="loadingStates.files" class="file-item" style="justify-content: center; font-style: italic">Loading files...</div>

              <div v-else v-for="file in filteredFiles" :key="file.TypeId" class="file-item">
                <input type="checkbox" :value="file.TypeId" v-model="selectedFiles" @click.stop />
                <div class="file-info" @click="toggleFileSelection(file.TypeId)">
                  <img :src="pdfIcon" alt="PDF Icon" class="file-icon" />
                  <span class="file-name">{{ file.DisplayName }}</span>
                </div>
              </div>

              <p v-if="!loadingStates.files && filteredFiles.length === 0">No files found.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="cancel-button" @click="$emit('close')">Cancel</button>
        <button class="confirm-button" @click="handleImport" :disabled="selectedFiles.length === 0">Import Selected</button>
      </div>
    </div>
  </div>
</template>

<script>
import { showToast } from '@/utils/toast' // Adjust if needed
import { get } from '@/utils/api'
import pdfIcon from '@/assets/pdficon.png'

export default {
  name: 'VasionImportModal',
  props: {
    show: { type: Boolean, required: true },
    tenantId: { type: Number, required: true },
  },
  data() {
    return {
      pdfIcon,
      userTenantId: null,
      selectedFolder: 4, // default folder ID based on your example
      selectedFiles: [],
      vasionFolders: [],
      files: [],
      loadingStates: {
        files: false,
        vasionFolders: false,
        tenantConfig: false,
      },
      tenantConfig: {
        selected_theme: 'Maxxified',
        theme_primary_color: '#164863',
        theme_secondary_color: '#e4f3fb',
        classification_confidence_level: '',
        recognize_confidence_level: '',
        iu_limit: '',
        vasion_api_token: '',
        vasion_api_url: '',
        url_name: '',
        logo: '',
        vasion_folder_id: '',
        custom_primary_color: '',
        custom_secondary_color: '',
      },
      searchQuery: '',
    }
  },
  watch: {
    selectedFolder(newFolderId) {
      if (newFolderId) {
        this.fetchFiles(newFolderId)
      }
    },
  },
  mounted() {
    const userData = JSON.parse(localStorage.getItem('UserData'))
    const tenantId = userData?.tenant_id
    this.userTenantId = tenantId

    this.fetchTenantConfig(tenantId)
  },
  computed: {
    filteredFiles() {
      if (!this.searchQuery) return this.files
      return this.files.filter((file) => file.DisplayName.toLowerCase().includes(this.searchQuery.toLowerCase()))
    },
    areAllFilesSelected() {
      return this.filteredFiles.length > 0 && this.filteredFiles.every((file) => this.selectedFiles.includes(file.TypeId))
    },
  },

  methods: {
    selectFolder(folderId) {
      this.selectedFolder = folderId
      this.selectedFiles = []
      this.fetchFiles(folderId)
    },
    toggleSelectAll() {
      const allSelected = this.areAllFilesSelected
      if (allSelected) {
        // Deselect all filtered files
        this.selectedFiles = this.selectedFiles.filter((id) => !this.filteredFiles.map((f) => f.TypeId).includes(id))
      } else {
        // Add all filtered files
        const filteredIds = this.filteredFiles.map((file) => file.TypeId)
        this.selectedFiles = Array.from(new Set([...this.selectedFiles, ...filteredIds]))
      }
    },

    async fetchTenantConfig(tenantId) {
      this.loadingStates.tenantConfig = true

      try {
        const configRes = await get(`/v1/tenant-configs/tenant/${tenantId}`)
        const configList = configRes?.data?.data

        if (!Array.isArray(configList)) {
          showToast({ title: 'Error', message: 'Invalid tenant config format', type: 'error' })
          return
        }

        configList.forEach(({ key, value }) => {
          this.tenantConfig[key] = value
        })

        await this.fetchVasionFolders()

        const defaultFolderId = this.tenantConfig.vasion_folder_id
        if (defaultFolderId) {
          this.selectedFolder = parseInt(defaultFolderId)
          await this.fetchFiles(defaultFolderId)
        }
      } catch (err) {
        showToast({ title: 'Error', message: 'Failed to fetch tenant config', type: 'error' })
      } finally {
        this.loadingStates.tenantConfig = false
      }
    },

    async fetchVasionFolders() {
      const { vasion_api_url, vasion_api_token } = this.tenantConfig
      if (!vasion_api_url || !vasion_api_token) {
        showToast({ title: 'Error', message: 'Missing Vasion API URL or Token', type: 'error' })
        return
      }

      try {
        this.loadingStates.vasionFolders = true
        const folderRes = await get(`/v1/vasion/folders`, {
          url: vasion_api_url,
          token: vasion_api_token,
        })
        const rawFolders = folderRes?.data?.data?.FolderList?.Values || []
        this.vasionFolders = rawFolders.map((folder) => ({
          id: folder.FolderId,
          name: folder.Name,
        }))
      } catch (err) {
        showToast({ title: 'Error', message: 'Failed to fetch folders from Vasion', type: 'error' })
      } finally {
        this.loadingStates.vasionFolders = false
      }
    },

    toggleFileSelection(typeId) {
      const index = this.selectedFiles.indexOf(typeId)
      if (index === -1) this.selectedFiles.push(typeId)
      else this.selectedFiles.splice(index, 1)
    },
    async fetchFiles(folderId) {
      this.loadingStates.files = true
      this.files = []

      try {
        const res = await get(`/v1/vasion/files`, {
          folderId,
          page: 1,
          pageSize: 999999,
          tenantId: this.userTenantId,
        })

        const items = res?.data?.data?.Items
        this.files = Array.isArray(items) ? items : []
      } catch (err) {
        showToast({
          title: 'Error',
          message: 'Failed to fetch Vasion files',
          type: 'error',
        })
        this.files = []
      } finally {
        this.loadingStates.files = false
      }
    },
    handleImport() {
      const selected = this.files.filter((file) => this.selectedFiles.includes(file.TypeId))
      this.$emit('import', selected)
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-content {
  width: 60%;
  height: 80%;
  background: white;
  padding: 24px;
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  box-shadow: 0px 4px 13.1px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
}

.modal-body-scrollable {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  /* optional: avoid scrollbar overlapping content */
}

.modal-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  background-color: #fff;
}

.select-all-container strong {
  font-weight: 700;
  color: var(--primary-color);
}

.modal-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
}

.modal-header-icon {
  display: flex;
  width: 100px;
  height: 100px;
  padding: var(--Micro-XL, 12px);
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  background: #f0f9fd;
}

.modal-header-title {
  color: var(--primary-color);
  text-align: center;
  font-family: Archivo;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
  margin-top: 20px;
}

.vasion-import-content {
  height: 100%;
  display: flex;
  gap: 24px;
  padding: 8px;
}

.select-all-container {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.vasionFolders-section,
.files-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
}

.section-title {
  color: var(--primary-color);
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
}

.vasionFolders-list,
.files-list {
  flex: 1;
  overflow-y: auto;
}

.folder-item,
.file-item {
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.folder-item:hover,
.file-item:hover {
  background-color: #f5f5f5;
}

.folder-item.active {
  background-color: #f0f9fd;
  color: var(--primary-color);
}

.file-item.selected {
  background-color: #f0f9fd;
  border: 1px solid #7ac4eb;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-start !important;
}

.file-name {
  font-size: 14px;
  color: #32393e;
}

.file-size {
  font-size: 12px;
  color: #666;
}

.cancel-button,
.confirm-button {
  width: 123.915px;
  height: 35px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 2px solid var(--primary-color);
  color: #fff;
  text-align: center;
  font-family: Archivo;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.cancel-button {
  background: white;
  border: 1px solid #e8e8e8;
  color: var(--primary-color);
}

.confirm-button {
  background: var(--primary-color);
  border: none;
  color: white;
}

.confirm-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.cancel-button:hover {
  background-color: #f5f5f5;
}

.confirm-button:hover:not(:disabled) {
  background-color: #123a50;
}
</style>
